<template>
  <aside ref="sidebar" class="contact-sidebar" :lang="isZh ? 'zh-CN' : 'en'" :aria-label="isZh ? '联系 ANOVATOR' : 'Contact ANOVATOR'" :style="{ '--contact-channel-count': channels.length, top: railTop === null ? undefined : `${railTop}px` }" @pointerenter="cancelClose" @pointerleave="leaveSidebar" @focusout="handleFocusOut" @keydown.esc.stop.prevent="close(true)">
    <div class="contact-sidebar__rail">
      <div class="contact-sidebar__brand">
        <span class="contact-sidebar__icon"><img src="/static/images/home-v2/contact-headset.svg" alt=""></span>
        <span>{{ isZh ? '联系' : 'Contact' }}<br>{{ isZh ? '我们' : 'Us' }}</span>
      </div>
      <div v-for="(channel, index) in channels" :key="channel.id" class="contact-sidebar__channel">
        <button :id="`contact-sidebar-${channel.id}`" class="contact-sidebar__trigger" :class="{ 'is-active': activeId === channel.id }" :data-contact-channel="channel.id" type="button" :aria-label="channel.label" :aria-expanded="activeId === channel.id" aria-controls="contact-sidebar-panel" @pointerenter="hoverChannel(channel.id, $event)" @focus="focusChannel(channel.id)" @click="show(channel.id)">
          <span class="contact-sidebar__icon"><img :src="channel.icon" alt=""></span>
        </button>
        <section v-if="activeId === channel.id" id="contact-sidebar-panel" class="contact-sidebar__panel" :class="`contact-sidebar__panel--${channel.id}`" :style="{ top: `${panelTop(channel.id, index)}px` }" :tabindex="channel.id === 'wechat' ? 0 : undefined" role="region" aria-labelledby="contact-sidebar-panel-label" @pointerenter="cancelClose">
          <template v-if="channel.id === 'wechat'">
            <h2 id="contact-sidebar-panel-label" class="contact-sidebar__sr-only">微信联系二维码</h2>
            <div class="contact-sidebar__wechat-image"><img :src="channel.qrImage" :alt="channel.value ? `微信：${channel.value}` : '微信联系二维码'" @error="failedQrImage = channel.qrImage"></div>
          </template>
          <template v-else-if="channel.id === 'whatsapp'">
            <h2 id="contact-sidebar-panel-label">WhatsApp</h2>
            <p class="contact-sidebar__value">{{ channel.value }}</p>
            <a class="contact-sidebar__whatsapp-chat" :href="channel.href" target="_blank" rel="noopener noreferrer">Chat on WhatsApp</a>
            <figure v-if="whatsappQr" class="contact-sidebar__whatsapp-qr">
              <img :src="whatsappQr" alt="WhatsApp chat QR code" width="176" height="176">
              <figcaption>Scan to chat on WhatsApp.</figcaption>
            </figure>
          </template>
          <template v-else-if="channel.id === 'linkedin'">
            <h2 id="contact-sidebar-panel-label">LinkedIn</h2>
            <p class="contact-sidebar__value">{{ channel.value }}</p>
            <a class="contact-sidebar__linkedin-visit" :href="channel.href" target="_blank" rel="noopener noreferrer">Visit LinkedIn</a>
          </template>
          <template v-else>
            <h2 id="contact-sidebar-panel-label">{{ channel.id === 'phone' ? (isZh ? '联系电话' : 'Call us') : 'Email us' }}</h2>
            <template v-if="channel.id === 'phone'">
              <button class="contact-sidebar__value contact-sidebar__copy-number" type="button" :aria-label="isZh ? `复制电话号码 ${channel.value}` : `Copy phone number ${channel.value}`" @click="copyPhone(channel.value)">{{ channel.value }}</button>
              <span class="contact-sidebar__copy-hint" role="status">{{ copyHint }}</span>
            </template>
            <p v-else class="contact-sidebar__value">{{ channel.value }}</p>
            <a v-if="channel.id === 'email'" :href="channel.href">Send an email</a>
          </template>
        </section>
      </div>
    </div>
  </aside>
</template>

<script>
import { contactWayAPI } from '@/utils/api'
import QRCode from 'qrcode'
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')
const { getContactChannels } = require('@/utils/contact-sidebar.cjs')

export default {
  name: 'ContactSidebar',
  mixins: [siteLanguageMixin],
  data() {
    return { contact: {}, contactRequestGuard: createLatestSiteRequestGuard(), activeId: null, closeTimer: null, suppressFocus: false, failedQrImage: '', whatsappQr: '', panelOffset: null, railTop: null, layoutFrame: null, layoutObserver: null, copyState: '' }
  },
  computed: {
    isZh() { return this.currentLanguage === 'ZH' },
    channels() { return getContactChannels(this.currentLanguage, this.contact).filter(channel => !channel.qrImage || channel.qrImage !== this.failedQrImage) },
    whatsappHref() { return this.channels.find(channel => channel.id === 'whatsapp')?.href || '' },
    copyHint() {
      if (this.copyState === 'copied') return this.isZh ? '已复制' : 'Copied'
      if (this.copyState === 'failed') return this.isZh ? '请选中号码复制' : 'Select the number to copy'
      return this.isZh ? '点击复制' : 'Click to copy'
    }
  },
  created() { this.loadContactWay() },
  mounted() {
    document.addEventListener('pointerdown', this.handleOutside)
    window.addEventListener('resize', this.scheduleLayout)
    window.addEventListener('scroll', this.scheduleLayout, { passive: true })
    window.visualViewport?.addEventListener('resize', this.scheduleLayout)
    this.layoutObserver = new ResizeObserver(this.scheduleLayout)
    this.layoutObserver.observe(document.getElementById('app'))
    this.scheduleLayout()
  },
  beforeUnmount() {
    this.cancelClose()
    document.removeEventListener('pointerdown', this.handleOutside)
    cancelAnimationFrame(this.layoutFrame)
    this.layoutObserver?.disconnect()
    window.removeEventListener('resize', this.scheduleLayout)
    window.removeEventListener('scroll', this.scheduleLayout)
    window.visualViewport?.removeEventListener('resize', this.scheduleLayout)
  },
  watch: {
    currentLanguage() { this.close(); this.loadContactWay() },
    '$route.fullPath'() { this.close(); this.$nextTick(this.scheduleLayout) },
    channels() { this.$nextTick(this.scheduleLayout) },
    whatsappHref: {
      immediate: true,
      async handler(href) {
        this.whatsappQr = ''
        this.panelOffset = null
        if (!href) {
          if (this.activeId === 'whatsapp') this.close()
          return
        }
        const qr = await QRCode.toDataURL(href, { width: 176, margin: 4 }).catch(() => '')
        if (this.whatsappHref !== href) return
        this.whatsappQr = qr
        this.$nextTick(this.fitPanel)
      }
    }
  },
  methods: {
    async loadContactWay() {
      const request = this.contactRequestGuard.start(this.currentLanguage)
      this.contact = {}
      this.failedQrImage = ''
      try {
        // Existing per-siteType Promise cache is shared with ContactModal and Footer.
        const contact = await contactWayAPI.getCurrent(request.siteType)
        if (this.contactRequestGuard.isCurrent(request, this.currentLanguage)) this.contact = contact || {}
      } catch (_) { /* Missing contact configuration does not create fallback contacts. */ }
    },
    panelTop(id, index) {
      if (this.panelOffset != null) return this.panelOffset
      if (this.isZh) return id === 'wechat' ? 66 + index * 65 : 92
      return 77.5 + index * 65
    },
    scheduleLayout() {
      if (this.layoutFrame !== null) return
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = null
        this.fitRail()
        this.$nextTick(this.fitPanel)
      })
    },
    fitRail() {
      const sidebar = this.$refs.sidebar
      if (!sidebar) return
      const rail = sidebar.getBoundingClientRect()
      const headerBottom = document.getElementById('header')?.getBoundingClientRect().bottom || 64
      const railStyle = getComputedStyle(sidebar)
      const inset = parseFloat(railStyle.scrollPaddingBottom) || 8
      const min = Math.max(8, headerBottom + 8)
      const max = Math.max(min, window.innerHeight - inset - rail.height)
      const headerHeight = parseFloat(railStyle.getPropertyValue('--global-header-height')) || 64
      const defaultTop = window.innerHeight <= 560 ? min : window.innerWidth <= 700 ? window.innerHeight * 0.35 : Math.max(headerHeight + 64, Math.min(236, window.innerHeight - 192 - rail.height))
      const preferred = Math.max(min, Math.min(defaultTop, max))
      // Keep page CTAs usable as they pass the fixed rail while scrolling.
      const actions = [...document.querySelectorAll('main button, main a[class*="button"], main a[class*="cta"], [class*="subnav"] a, .pagination button, .news-pagination button')]
        .filter(element => !element.disabled && getComputedStyle(element).visibility !== 'hidden')
        .map(element => {
          const rect = element.getBoundingClientRect()
          const style = getComputedStyle(element)
          return { top: rect.top, bottom: rect.bottom, height: rect.height, width: rect.width, left: rect.left + (parseFloat(style.paddingLeft) || 0), right: rect.right - (parseFloat(style.paddingRight) || 0) }
        })
        .filter(rect => rect.width > 0 && rect.height > 0 && rect.height <= 80 && rect.right > rail.left && rect.left < rail.right && rect.bottom > min && rect.top < max + rail.height)
      const candidates = [preferred, min, max, ...actions.flatMap(rect => [rect.top - rail.height - 8, rect.bottom + 8])]
        .filter(top => top >= min && top <= max)
      const overlap = top => actions.reduce((total, rect) => total + Math.max(0, Math.min(top + rail.height, rect.bottom) - Math.max(top, rect.top)), 0)
      candidates.sort((a, b) => overlap(a) - overlap(b) || Math.abs(a - preferred) - Math.abs(b - preferred))
      this.railTop = candidates[0]
    },
    fitPanel() {
      const sidebar = this.$refs.sidebar
      const panel = sidebar?.querySelector('.contact-sidebar__panel')
      if (!panel) return
      const railTop = sidebar.getBoundingClientRect().top
      const style = getComputedStyle(sidebar)
      const step = parseFloat(style.getPropertyValue('--contact-button-height')) + 1
      const brand = parseFloat(style.getPropertyValue('--contact-brand-height'))
      const panelHeight = panel.getBoundingClientRect().height
      const index = this.channels.findIndex(channel => channel.id === this.activeId)
      const preferred = this.isZh ? (this.activeId === 'wechat' ? brand - 38 + index * step : brand - 12) : brand - 26.5 + index * step
      const headerBottom = document.getElementById('header')?.getBoundingClientRect().bottom || 64
      const bottomInset = parseFloat(style.scrollPaddingBottom) || 16
      this.panelOffset = Math.max(headerBottom + 8 - railTop, Math.min(preferred, window.innerHeight - railTop - panelHeight - bottomInset))
    },
    async copyPhone(value) {
      try { await navigator.clipboard.writeText(value); this.copyState = 'copied' }
      catch (_) { this.copyState = 'failed' }
      this.$nextTick(this.fitPanel)
    },
    cancelClose() { clearTimeout(this.closeTimer); this.closeTimer = null },
    show(id) {
      this.cancelClose()
      if (this.activeId !== id) { this.panelOffset = null; this.copyState = '' }
      this.activeId = id
      this.$nextTick(this.fitPanel)
    },
    hoverChannel(id, event) { if (event.pointerType === 'mouse' && window.innerWidth > 700) this.show(id) },
    focusChannel(id) { if (!this.suppressFocus) this.show(id) },
    leaveSidebar(event) {
      if (event.pointerType !== 'mouse') return
      this.cancelClose()
      this.closeTimer = setTimeout(() => {
        if (!this.$refs.sidebar?.contains(document.activeElement)) this.close()
      }, 140)
    },
    close(restoreFocus = false) {
      this.cancelClose()
      const id = this.activeId
      this.activeId = null
      this.panelOffset = null
      this.copyState = ''
      if (restoreFocus && id) this.$nextTick(() => {
        this.suppressFocus = true
        this.$refs.sidebar?.querySelector(`[data-contact-channel="${id}"]`)?.focus({ preventScroll: true })
        this.suppressFocus = false
      })
    },
    handleFocusOut(event) { if (!this.$refs.sidebar?.contains(event.relatedTarget)) this.close() },
    handleOutside(event) { if (!this.$refs.sidebar?.contains(event.target)) this.close() }
  }
}
</script>

<style>
.contact-sidebar, .contact-sidebar * { box-sizing: border-box; }
.contact-sidebar { --contact-size: 64px; --contact-button-height: 64px; --contact-brand-height: 104px; --contact-icon-size: 24px; --contact-panel-gap: 16px; --contact-safe-bottom: max(16px, env(safe-area-inset-bottom)); --contact-rail-height: calc(var(--contact-brand-height) + var(--contact-channel-count) * (var(--contact-button-height) + 1px)); position: fixed; z-index: 998; top: max(calc(var(--global-header-height, 44px) + 64px), min(236px, calc(100dvh - 192px - var(--contact-rail-height)))); right: max(16px, env(safe-area-inset-right)); width: var(--contact-size); scroll-padding-bottom: var(--contact-safe-bottom); font-family: 'MiSans-Regular', 'Segoe UI', sans-serif; color: #1d1d1f; }
.contact-sidebar__rail { display: flex; flex-direction: column; gap: 1px; border-radius: 12px; background: #d9d9de; box-shadow: 0 8px 12px rgba(0,0,0,.12); }
.contact-sidebar__brand, .contact-sidebar__trigger { display: flex; width: var(--contact-size); padding: 0; border: 0; align-items: center; justify-content: center; background: #fff; color: #1d1d1f; }
.contact-sidebar__brand { height: var(--contact-brand-height); flex-direction: column; gap: 8px; border-radius: 12px 12px 0 0; font: 700 12px/18px 'MiSans-Bold', 'Segoe UI', sans-serif; text-align: center; }
.contact-sidebar:lang(zh-CN) .contact-sidebar__brand { font-size: 14px; line-height: 1.45; }
.contact-sidebar__trigger { height: var(--contact-button-height); min-height: 44px; cursor: pointer; touch-action: manipulation; }
a.contact-sidebar__trigger:hover { color: #1d1d1f; }
.contact-sidebar__channel:last-child .contact-sidebar__trigger { border-radius: 0 0 12px 12px; }
.contact-sidebar__icon { display: flex; width: var(--contact-icon-size); height: var(--contact-icon-size); align-items: center; justify-content: center; }
.contact-sidebar__icon img { display: block; flex: none; }
.contact-sidebar__trigger img { filter: brightness(0); }
.contact-sidebar__trigger.is-active, .contact-sidebar__trigger:focus-visible { background: #ef0a6a; }
.contact-sidebar__trigger.is-active img, .contact-sidebar__trigger:focus-visible img { filter: brightness(0) invert(1); }
.contact-sidebar__trigger[data-contact-channel='whatsapp']:hover, .contact-sidebar__trigger[data-contact-channel='linkedin']:hover { background: #ef0a6a; }
.contact-sidebar__trigger[data-contact-channel='whatsapp']:hover img, .contact-sidebar__trigger[data-contact-channel='linkedin']:hover img { filter: brightness(0) invert(1); }
.contact-sidebar__trigger[data-contact-channel='whatsapp'] img, .contact-sidebar__trigger[data-contact-channel='linkedin'] img { width: var(--contact-icon-size); height: var(--contact-icon-size); }
.contact-sidebar__panel:focus-visible, .contact-sidebar__panel a:focus-visible { outline: 2px solid #ef0a6a; outline-offset: -4px; }
.contact-sidebar__trigger:focus-visible { outline: 2px solid #fff; outline-offset: -5px; }
.contact-sidebar__panel { position: absolute; right: calc(100% + var(--contact-panel-gap)); display: flex; width: 264px; max-width: calc(100vw - var(--contact-size) - var(--contact-panel-gap) - max(16px, env(safe-area-inset-right)) - max(8px, env(safe-area-inset-left))); max-height: calc(100dvh - var(--global-header-height, 64px) - 24px - var(--contact-safe-bottom)); overflow-x: hidden; overflow-y: auto; overscroll-behavior: contain; flex-direction: column; align-items: flex-start; gap: 12px; padding: 16px; border-radius: 12px; background: #fff; box-shadow: 0 8px 24px rgba(0,0,0,.12); }
.contact-sidebar__channel:has(.contact-sidebar__panel)::before { position: absolute; top: 0; bottom: 0; right: 100%; width: var(--contact-panel-gap); content: ''; }
.contact-sidebar__panel > * { flex-shrink: 0; }
.contact-sidebar__panel--email { width: 282px; }
.contact-sidebar__panel h2, .contact-sidebar__panel p { margin: 0; text-transform: none; }
.contact-sidebar__panel h2, .contact-sidebar__panel a { font: 400 12px/1.45 'MiSans-Regular', 'Segoe UI', sans-serif; color: #6e6e73; }
.contact-sidebar__panel .contact-sidebar__value { max-width: 100%; margin: 0; font: 700 20px/1.45 'MiSans-Bold', 'Segoe UI', sans-serif; color: #1d1d1f; overflow-wrap: anywhere; }
.contact-sidebar__panel a { text-decoration: none; }
.contact-sidebar__panel a:hover { color: #ef0a6a; }
.contact-sidebar__copy-number { min-height: 44px; padding: 0; border: 0; background: transparent; text-align: left; cursor: pointer; user-select: text; }
.contact-sidebar__copy-number:focus-visible { outline: 2px solid #ef0a6a; outline-offset: 2px; }
.contact-sidebar__copy-hint { color: #6e6e73; font-size: 12px; line-height: 1.45; }
.contact-sidebar__panel .contact-sidebar__whatsapp-chat, .contact-sidebar__panel .contact-sidebar__linkedin-visit { display: grid; width: 100%; min-height: 44px; padding: 8px; place-items: center; border-radius: 12px; background: #d6075d; color: #fff; font-size: 14px; font-weight: 700; }
.contact-sidebar__panel .contact-sidebar__whatsapp-chat:hover, .contact-sidebar__panel .contact-sidebar__linkedin-visit:hover { color: #fff; }
.contact-sidebar__panel .contact-sidebar__whatsapp-chat:focus-visible, .contact-sidebar__panel .contact-sidebar__linkedin-visit:focus-visible { outline-color: #1d1d1f; outline-offset: 3px; }
.contact-sidebar__whatsapp-qr { display: flex; width: 100%; margin: 0; flex-direction: column; align-items: center; gap: 8px; }
.contact-sidebar__whatsapp-qr img { display: block; width: 176px; max-width: 100%; height: auto; aspect-ratio: 1; }
.contact-sidebar__whatsapp-qr figcaption { color: #6e6e73; font-size: 12px; line-height: 1.45; text-align: center; }
.contact-sidebar__panel--wechat { align-items: center; border-radius: 24px; }
.contact-sidebar__wechat-image { position: relative; width: 134px; height: 224px; overflow: hidden; }
.contact-sidebar__wechat-image img { position: absolute; top: -7.02%; left: -7.5%; width: 115%; height: 113.04%; max-width: none; }
.contact-sidebar__sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
body:has(.contact-modal-overlay, .footer-social-dialog-overlay, .acm-root:not([hidden]), .mobile-nav-overlay) .contact-sidebar { visibility: hidden; }
.contact-sidebar:has(.contact-sidebar__panel) ~ .bottomtotop i { z-index: 997; transition-property: background-color, box-shadow; }
@media (min-width: 701px) and (max-width: 1023px) {
  .contact-sidebar { --contact-size: 56px; --contact-button-height: 56px; --contact-brand-height: 88px; --contact-icon-size: 22px; --contact-panel-gap: 12px; right: max(12px, env(safe-area-inset-right)); }
  .contact-sidebar__brand { gap: 6px; font-size: 11px; line-height: 16px; }
  .contact-sidebar:lang(zh-CN) .contact-sidebar__brand { font-size: 12px; }
}
@media (max-width: 1023px) {
  .contact-sidebar__trigger img { width: var(--contact-icon-size); height: var(--contact-icon-size); object-fit: contain; }
  .contact-sidebar__brand img { width: 18px; height: 18px; }
}
@media (max-width: 700px) {
  .contact-sidebar { --contact-size: 44px; --contact-button-height: 44px; --contact-brand-height: 64px; --contact-icon-size: 20px; --contact-panel-gap: 8px; --contact-safe-bottom: max(8px, env(safe-area-inset-bottom)); top: max(calc(var(--global-header-height, 64px) + 8px), min(35dvh, calc(100dvh - var(--contact-rail-height) - var(--contact-safe-bottom)))); right: max(8px, env(safe-area-inset-right)); }
  .contact-sidebar__brand { gap: 4px; font-size: 10px; line-height: 14px; }
  .contact-sidebar:lang(zh-CN) .contact-sidebar__brand { font-size: 11px; }
  .contact-sidebar__panel { max-width: calc(100vw - 44px - 8px - max(8px, env(safe-area-inset-right)) - max(8px, env(safe-area-inset-left))); gap: 10px; }
  .contact-sidebar__panel .contact-sidebar__value { font-size: 18px; }
  .contact-sidebar__panel a:not(.contact-sidebar__whatsapp-chat):not(.contact-sidebar__linkedin-visit) { display: flex; min-height: 44px; align-items: center; }
}
@media (max-height: 560px) {
  .contact-sidebar { --contact-brand-height: 56px; --contact-button-height: 44px; top: calc(var(--global-header-height, 64px) + 8px); }
  .contact-sidebar__brand { gap: 2px; font-size: 10px; line-height: 13px; }
  .contact-sidebar:lang(zh-CN) .contact-sidebar__brand { font-size: 11px; }
}
@media (max-width: 700px) and (max-height: 560px) {
  .contact-sidebar ~ .bottomtotop i { right: max(60px, calc(env(safe-area-inset-right) + 52px)); transition-property: background-color, box-shadow; }
}
</style>
